/**
 * Filter Builder Component
 * Main component for building complex filter queries with nested AND/OR groups
 * 
 * Following the reference pattern where filters are stored as:
 * - Field filter: { fieldName: { _operator: value } }
 * - Logic group: { _and: Filter[] } or { _or: Filter[] }
 */

import { useCallback, useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FilterNodes, type Filter } from './FilterNodes';
import type { Field } from '@/hooks/useFields';
import { useRelationalFields } from '@/hooks/useRelationalFields';
import { cn } from '@/lib/utils';
import { getDefaultOperator } from '@/utils/filter-operators';

interface FilterBuilderProps {
    value: Filter | null;
    fields: Field[];
    /** Providing collection enables relational field traversal in the picker */
    collection?: string | null;
    onChange: (filter: Filter | null) => void;
    className?: string;
    disabled?: boolean;
}

/**
 * Get the name (key) of a filter node
 */
export function getNodeName(node: Filter): string {
    if (!node || typeof node !== 'object') return '';
    return Object.keys(node)[0] || '';
}

/**
 * Get the field path from a filter node (handles nested fields like user.name)
 */
export function getField(node: Record<string, unknown>): string {
    const name = getNodeName(node);
    if (name.startsWith('_')) return '';
    if (!node[name] || typeof node[name] !== 'object') return '';

    const subFields = getField(node[name] as Record<string, unknown>);
    return subFields !== '' ? `${name}.${subFields}` : name;
}

/**
 * Get the comparator (operator) from a filter node
 */
export function getComparator(node: Record<string, unknown>): string {
    const fieldPath = getField(node);
    if (!fieldPath) return '';

    // Navigate to the field's value
    const parts = fieldPath.split('.');
    let current: unknown = node;
    for (const part of parts) {
        if (current && typeof current === 'object') {
            current = (current as Record<string, unknown>)[part];
        } else {
            return '';
        }
    }

    if (current && typeof current === 'object') {
        return Object.keys(current)[0] || '';
    }
    return '';
}

/**
 * Get the value from a filter node
 */
export function getNodeValue(node: Record<string, unknown>): unknown {
    const fieldPath = getField(node);
    const comparator = getComparator(node);
    if (!fieldPath || !comparator) return null;

    const parts = fieldPath.split('.');
    let current: unknown = node;
    for (const part of parts) {
        if (current && typeof current === 'object') {
            current = (current as Record<string, unknown>)[part];
        } else {
            return null;
        }
    }

    if (current && typeof current === 'object') {
        return (current as Record<string, unknown>)[comparator];
    }
    return null;
}

/**
 * Convert field + operator + value to filter format
 * e.g., fieldToFilter('user.name', '_eq', 'John') => { user: { name: { _eq: 'John' } } }
 */
export function fieldToFilter(field: string, operator: string, value: unknown): Filter {
    const sections = field.split('.');

    function buildNested(parts: string[]): Record<string, unknown> {
        const part = parts.shift();
        if (part !== undefined) {
            return { [part]: buildNested(parts) };
        } else {
            return { [operator]: value };
        }
    }

    return buildNested(sections);
}

export function FilterBuilder({
    value,
    fields,
    collection = null,
    onChange,
    className,
    disabled = false,
}: FilterBuilderProps) {
    // Extend fields with related-collection sub-fields for supported M2O relations
    const extendedFields = useRelationalFields(collection ?? null, fields);

    // The internal filter array - extract from _and wrapper if present
    const filters = useMemo((): Filter[] => {
        if (!value) return [];

        // If value is wrapped in _and (the top-level wrapper), extract it
        if (value._and && Array.isArray(value._and)) {
            return value._and;
        }

        // If value is wrapped in _or at top level
        if (value._or && Array.isArray(value._or)) {
            return value._or;
        }

        // If it's a single filter, wrap in array
        if (Object.keys(value).length > 0) {
            return [value];
        }

        return [];
    }, [value]);

    // Top level operator (_and or _or)
    const topOperator = useMemo((): '_and' | '_or' => {
        if (!value) return '_and';
        if (value._or && Array.isArray(value._or)) return '_or';
        return '_and';
    }, [value]);

    // Handle filter changes - wrap in top-level _and
    const handleFiltersChange = useCallback((newFilters: Filter[]) => {
        if (newFilters.length === 0) {
            onChange(null);
        } else {
            onChange({ [topOperator]: newFilters } as Filter);
        }
    }, [onChange, topOperator]);

    // Add a new field filter
    const addFilter = useCallback(() => {
        if (extendedFields.length === 0) return;

        const firstField = extendedFields[0];
        if (!firstField) return;

        const operator = `_${getDefaultOperator(firstField.type || 'string')}`;
        const newFilter = fieldToFilter(firstField.field, operator, null);

        handleFiltersChange([...filters, newFilter]);
    }, [extendedFields, filters, handleFiltersChange]);

    // Add a new AND group
    const addAndGroup = useCallback(() => {
        handleFiltersChange([...filters, { _and: [] }]);
    }, [filters, handleFiltersChange]);

    // Add a new OR group
    const addOrGroup = useCallback(() => {
        handleFiltersChange([...filters, { _or: [] }]);
    }, [filters, handleFiltersChange]);

    // Clear all filters
    const clearAll = useCallback(() => {
        onChange(null);
    }, [onChange]);

    const isEmpty = filters.length === 0;

    return (
        <div className={cn('p-3 border rounded-lg bg-muted/30', className)}>
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-muted-foreground">Filters</span>
                    {!isEmpty && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                            {filters.length} {filters.length === 1 ? 'rule' : 'rules'}
                        </span>
                    )}
                </div>
                {!isEmpty && (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-muted-foreground hover:text-destructive"
                        onClick={clearAll}
                        disabled={disabled}
                    >
                        <X className="h-3 w-3 mr-1" />
                        Clear All
                    </Button>
                )}
            </div>

            {/* Filter nodes */}
            {!isEmpty && (
                <FilterNodes
                    filters={filters}
                    fields={extendedFields}
                    onChange={handleFiltersChange}
                    depth={0}
                    disabled={disabled}
                />
            )}

            {/* Empty state / Add buttons */}
            <div className={cn('flex items-center gap-2', !isEmpty && 'mt-3 pt-3 border-t border-border/50')}>
                <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={addFilter}
                    disabled={disabled || extendedFields.length === 0}
                >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Filter
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={addAndGroup}
                    disabled={disabled}
                >
                    <Plus className="h-3 w-3 mr-1" />
                    Add AND Group
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={addOrGroup}
                    disabled={disabled}
                >
                    <Plus className="h-3 w-3 mr-1" />
                    Add OR Group
                </Button>
            </div>
        </div>
    );
}
